import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Loader2, Plus, Sparkles, Upload, User2, X, Search, Trash2, Eye, Play, Video, Mic } from 'lucide-react';
import MediaRecorderPanel from './MediaRecorderPanel';
import toast from 'react-hot-toast';
import { videoStudioService } from '../../services/videoStudioService';
import { HeygenAvatar } from '../../types/videoStudio';
import { useAiCost } from '../ai/AiCostBadge';
import { creditos } from '../../services/aiCreditsService';

interface CustomAvatarDraft {
  id: string; // local draft id while pending
  name: string;
  status: 'uploading' | 'pending' | 'ready' | 'failed';
  preview_image_url?: string;
  error?: string;
}

interface Props {
  onAvatarReady: (avatar: HeygenAvatar) => void;
  selectedId?: string | null;
}

// Lighter shape coming from /heygen/avatar-groups (cover only, not all looks)
interface AvatarGroup {
  key: string;
  name: string;
  look_count: number;
  cover_id: string;
  preview_image_url?: string;
  preview_video_url?: string;
  gender?: string;
  premium: boolean;
  is_yours: boolean;
  is_custom: boolean;
}

const AvatarStudio: React.FC<Props> = ({ onAvatarReady, selectedId }) => {
  const { t } = useTranslation();
  // Criar avatar debita dos créditos de IA (média real da ação, ou a estimativa)
  const { costCents: custoAvatar } = useAiCost('video', 'avatar_creation');
  // Backend-paginated groups (light) — full HeygenAvatar list never reaches the client
  const [groups, setGroups] = useState<AvatarGroup[]>([]);
  // Lazy-loaded looks for the currently-opened group
  const [loadingLooks, setLoadingLooks] = useState(false);
  const [drafts, setDrafts] = useState<CustomAvatarDraft[]>(() => {
    try { return JSON.parse(localStorage.getItem('heygen_drafts') || '[]'); } catch { return []; }
  });
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newGender, setNewGender] = useState<'male' | 'female' | 'unknown'>('unknown');
  // New Digital Twin flow — replaces single-photo upload
  const [trainingVideo, setTrainingVideo] = useState<File | null>(null);
  const [consentVideo, setConsentVideo] = useState<File | null>(null);
  const [voiceAudio, setVoiceAudio] = useState<File | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Which slot is currently recording (controls MediaRecorderPanel)
  const [recording, setRecording] = useState<null | 'training' | 'consent' | 'voice'>(null);
  const trainRef = useRef<HTMLInputElement>(null);
  const consentRef = useRef<HTMLInputElement>(null);
  const voiceRef = useRef<HTMLInputElement>(null);

  // Pagination + filters
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [owned, setOwned] = useState<'all' | 'only' | 'stock'>('all');
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  // Preview modal — when set, shows the avatar's HeyGen preview video / image at full size
  const [previewAvatar, setPreviewAvatar] = useState<HeygenAvatar | null>(null);
  // Looks picker — HeyGen often returns the same person under multiple avatar_ids
  // (different outfits / poses). When the user clicks a card with siblings, we
  // open this picker to show every look so they can choose the specific one.
  const [looksGroup, setLooksGroup] = useState<{ name: string; looks: HeygenAvatar[] } | null>(null);

  useEffect(() => {
    localStorage.setItem('heygen_drafts', JSON.stringify(drafts));
  }, [drafts]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Loads grouped avatars from the backend — light payload (24 cards/page)
  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const r = await videoStudioService.listAvatarGroups({
        page,
        limit: 24,
        search: search || undefined,
        owned: owned === 'all' ? undefined : owned
      });
      if (r.success) {
        setGroups(prev => page === 1 ? r.data : [...prev, ...r.data]);
        setTotal(r.total);
        setTotalPages(r.totalPages);
      }
    } catch { /* empty state covers it */ }
    finally { setLoading(false); }
  }, [page, search, owned]);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  // Click on a group card → fetch its full looks list and open the picker
  const openLooks = async (group: AvatarGroup) => {
    setLoadingLooks(true);
    setLooksGroup({ name: group.name, looks: [] });
    try {
      const r = await videoStudioService.listAvatarGroupLooks(group.key);
      if (r.success) setLooksGroup({ name: group.name, looks: r.data });
    } catch {
      setLooksGroup(null);
    } finally {
      setLoadingLooks(false);
    }
  };

  const handleDeleteAvatar = async (a: HeygenAvatar, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!a.is_yours) return;
    if (!window.confirm(t('videoStudio.avatar.deleteConfirm', { name: a.name }) as string)) return;
    try {
      await videoStudioService.deleteAvatar(a.id);
      // Remove the deleted avatar's group from the paginated list (group was
      // single-look, so the whole card disappears). Multi-look groups are rare
      // for user-owned avatars — just refetch on next pagination.
      setGroups(prev => prev.filter(g => g.cover_id !== a.id));
      setTotal((tot) => Math.max(0, tot - 1));
      toast.success(t('videoStudio.avatar.deleted') as string);
    } catch (err: any) {
      toast.error(err.response?.data?.message || (t('videoStudio.avatar.deleteFailed') as string));
    }
  };

  // Poll pending drafts
  useEffect(() => {
    const pending = drafts.filter(d => d.status === 'pending');
    if (pending.length === 0) return;
    const t = setInterval(async () => {
      for (const draft of pending) {
        try {
          const r = await videoStudioService.getAvatarStatus(draft.id);
          if (r.success && r.data) {
            const status = String(r.data.status || '').toLowerCase();
            if (status === 'completed' || status === 'ready' || status === 'success') {
              setDrafts(prev => prev.filter(d => d.id !== draft.id));
              // Insert the new avatar as its own group at the top
              setGroups(prev => [{
                key: draft.id,
                name: draft.name,
                look_count: 1,
                cover_id: draft.id,
                preview_image_url: r.data?.preview_image_url || draft.preview_image_url,
                preview_video_url: undefined,
                gender: undefined,
                premium: false,
                is_yours: true,
                is_custom: true
              }, ...prev]);
              toast.success(`Avatar "${draft.name}" pronto`);
            } else if (status === 'failed' || status === 'error') {
              setDrafts(prev => prev.map(d => d.id === draft.id ? { ...d, status: 'failed', error: 'Falha ao processar' } : d));
            }
          }
        } catch { /* silent */ }
      }
    }, 8000);
    return () => clearInterval(t);
  }, [drafts]);

  const handleSubmitCreate = async () => {
    if (!newName.trim()) { toast.error(t('videoStudio.avatar.create.errName', 'Dê um nome ao avatar') as string); return; }
    if (!trainingVideo) { toast.error(t('videoStudio.avatar.create.errTraining', 'Envie o vídeo de treinamento (2–5min)') as string); return; }
    if (!consentVideo)  { toast.error(t('videoStudio.avatar.create.errConsent', 'Envie o vídeo de consentimento') as string); return; }
    if (!consentChecked) { toast.error(t('videoStudio.avatar.create.errConsentBox', 'Marque o termo de consentimento') as string); return; }

    setSubmitting(true);
    setUploadProgress(0);
    try {
      const r = await videoStudioService.createAvatar(
        {
          name: newName,
          gender: newGender,
          trainingVideo,
          consentVideo,
          voiceAudio: voiceAudio || null
        },
        (pct) => setUploadProgress(pct)
      );
      if (r.success && r.data?.avatar) {
        setDrafts(prev => [{
          id: r.data!.avatar.heygen_id,
          name: newName,
          status: 'pending'
        }, ...prev]);
        const voiceMsg = r.data.voice?.elevenlabs_id
          ? ' · ' + t('videoStudio.avatar.create.voiceCloned', 'voz clonada com ElevenLabs')
          : '';
        toast.success(t('videoStudio.avatar.create.submitted', 'Avatar enviado para treinamento') + voiceMsg);
        setShowCreate(false);
        setNewName(''); setTrainingVideo(null); setConsentVideo(null); setVoiceAudio(null); setConsentChecked(false);
      }
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || (t('videoStudio.avatar.create.failed', 'Falha ao criar avatar') as string);
      toast.error(msg);
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  // File-size + duration sanity checks. Audio extraction from training video
  // would require ffmpeg server-side; we skip and just allow optional upload.
  const validateVideoFile = (file: File, maxMb = 500, minSec = 0): string | null => {
    if (!/^video\//.test(file.type)) return t('videoStudio.avatar.create.errVideoFormat', 'Formato inválido — envie MP4/MOV') as string;
    if (file.size > maxMb * 1024 * 1024) return t('videoStudio.avatar.create.errVideoSize', 'Arquivo maior que {{mb}}MB', { mb: maxMb }) as string;
    return null;
  };
  const validateAudioFile = (file: File, maxMb = 50): string | null => {
    if (!/^audio\//.test(file.type)) return t('videoStudio.avatar.create.errAudioFormat', 'Formato inválido — envie MP3/WAV/M4A') as string;
    if (file.size > maxMb * 1024 * 1024) return t('videoStudio.avatar.create.errAudioSize', 'Áudio maior que {{mb}}MB', { mb: maxMb }) as string;
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('videoStudio.avatar.libraryTitle')}</p>
          <p className="text-xs text-gray-500">{t('videoStudio.avatar.libraryHint')}</p>
        </div>
        {/* Create-avatar button — hidden until our HeyGen account has Enterprise tier
            (Digital Twin via /v2/video_avatar requires Enterprise; currently 403). */}
        {false && (
          <button
            onClick={() => setShowCreate(true)}
            className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-primary-500 to-purple-600 text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:brightness-110"
          >
            <Plus className="w-3.5 h-3.5" /> {t('videoStudio.avatar.createBtn', 'Criar avatar')}
          </button>
        )}
      </div>

      {drafts.length > 0 && (
        <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-3">
          <p className="text-xs font-semibold text-amber-300 mb-2">{t('videoStudio.avatar.processing', { count: drafts.length })}</p>
          <div className="flex gap-2 overflow-x-auto">
            {drafts.map(d => (
              <div key={d.id} className="flex-shrink-0 w-24 rounded-lg overflow-hidden border border-amber-500/20 relative">
                {d.preview_image_url ? (
                  <img src={d.preview_image_url} alt={d.name} className="w-full aspect-[3/4] object-cover opacity-50" />
                ) : (
                  <div className="aspect-[3/4] bg-gray-900" />
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  {d.status === 'failed'
                    ? <X className="w-5 h-5 text-rose-400" />
                    : <Loader2 className="w-5 h-5 text-amber-300 animate-spin" />}
                </div>
                <p className="text-[10px] text-gray-900 dark:text-white text-center py-1 px-1 truncate bg-gray-50 dark:bg-gray-950/90">{d.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search + filter bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('videoStudio.avatar.searchPlaceholder', { count: total || 0 }) as string}
            className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-xs focus:outline-none focus:border-primary-500/50"
          />
        </div>
        <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800">
          {(['all', 'only', 'stock'] as const).map(opt => (
            <button
              key={opt}
              onClick={() => { setOwned(opt); setPage(1); }}
              className={`px-2.5 py-1.5 text-[11px] font-semibold transition ${
                owned === opt
                  ? 'bg-primary-500/20 text-primary-700 dark:text-primary-300'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5'
              }`}
            >
              {opt === 'all' ? t('videoStudio.avatar.filterAll') : opt === 'only' ? t('videoStudio.avatar.filterMine') : t('videoStudio.avatar.filterStock')}
            </button>
          ))}
        </div>
      </div>

      {/* Backend-paginated groups: 24 lightweight cards per page (no full looks
          payload). Click → openLooks(group) lazy-loads that group's looks. */}
      {/* Loading state — shown while the (potentially slow first) request runs.
          The first call after server boot can take ~30s because HeyGen has 6.8k+
          avatars to enumerate; subsequent calls hit the 1h backend cache. */}
      {loading && groups.length === 0 && (
        <div className="p-12 text-center rounded-xl bg-gray-50 dark:bg-gray-900/30 border border-dashed border-gray-200 dark:border-gray-800">
          <Loader2 className="w-10 h-10 text-primary-500 animate-spin mx-auto mb-3" />
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {t('videoStudio.avatar.loading', 'Carregando biblioteca de avatares…')}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            {t('videoStudio.avatar.loadingHint', 'A primeira carga pode levar até 30 segundos. Depois fica instantâneo.')}
          </p>
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
        {groups.length === 0 && drafts.length === 0 && !loading && (
          <div className="col-span-full p-8 text-center rounded-xl bg-gray-50 dark:bg-gray-900/30 border border-dashed border-gray-200 dark:border-gray-800">
            <User2 className="w-10 h-10 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">{t('videoStudio.avatar.empty')}</p>
          </div>
        )}
        {groups.map(g => {
          const isSelected = selectedId === g.cover_id;
          return (
            <div
              key={g.key}
              onClick={() => openLooks(g)}
              className={`group relative aspect-[3/4] rounded-xl overflow-hidden border-2 cursor-pointer transition-all ${
                isSelected
                  ? 'border-primary-500 ring-4 ring-primary-500/40 scale-[1.02] shadow-lg shadow-primary-500/30'
                  : 'border-gray-200 dark:border-gray-800 hover:border-primary-500/60 hover:scale-[1.02]'
              }`}
            >
              {g.preview_image_url ? (
                <img
                  src={g.preview_image_url}
                  alt={g.name}
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  className={`w-full h-full object-cover transition ${isSelected ? '' : 'group-hover:scale-105'}`}
                />
              ) : (
                <div className="w-full h-full bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
                  <User2 className="w-6 h-6 text-gray-400" />
                </div>
              )}

              {isSelected && (
                <div className="absolute inset-0 bg-primary-500/10 pointer-events-none flex items-start justify-end p-1.5">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-500 text-white text-xs font-bold shadow-lg">✓</span>
                </div>
              )}

              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-gray-950/95 to-transparent p-1.5">
                <p className="text-[10px] font-semibold text-white truncate">{g.name}</p>
              </div>

              {g.is_yours && (
                <span className="absolute top-1 left-1 text-[9px] px-1 py-0.5 rounded bg-emerald-500/90 text-emerald-950 font-bold pointer-events-none">{t('videoStudio.avatar.youBadge')}</span>
              )}
              {g.premium && !g.is_yours && (
                <span className="absolute top-1 left-1 text-[9px] px-1 py-0.5 rounded bg-amber-500/90 text-amber-950 font-bold pointer-events-none">{t('videoStudio.avatar.proBadge')}</span>
              )}

              {/* Multi-look counter — shown when the same name has more than one avatar_id */}
              {g.look_count > 1 && (
                <span className="absolute top-1 right-1 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-violet-500/95 text-white font-bold pointer-events-none">
                  {g.look_count} {t('videoStudio.avatar.looks', 'visuais')}
                </span>
              )}

              {/* Preview button — shown on hover. Uses cover info, no full look fetch needed. */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewAvatar({
                    id: g.cover_id, name: g.name,
                    preview_image_url: g.preview_image_url,
                    preview_video_url: g.preview_video_url,
                    gender: g.gender, premium: g.premium, is_yours: g.is_yours
                  } as any);
                }}
                className="absolute bottom-1 left-1 p-1.5 rounded-md bg-black/70 text-white shadow-md hover:bg-black/90 transition opacity-0 group-hover:opacity-100"
                title={(g.preview_video_url ? t('videoStudio.avatar.previewVideoTitle') : t('videoStudio.avatar.previewPhotoTitle')) as string}
              >
                {g.preview_video_url ? <Play className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </button>
            </div>
          );
        })}
      </div>

      {/* Looks picker modal — shown when the user clicks an avatar with multiple looks */}
      {looksGroup && (
        <div
          className="fixed inset-0 z-[160] flex items-center justify-center p-4 bg-gray-950/85 backdrop-blur-sm"
          onClick={() => setLooksGroup(null)}
        >
          <div
            className="w-full max-w-3xl max-h-[88vh] flex flex-col bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">{looksGroup.name}</h3>
                <p className="text-[11px] text-gray-500">
                  {t('videoStudio.avatar.looksHint', '{{count}} visuais disponíveis · escolha o que melhor combina com seu vídeo', { count: looksGroup.looks.length })}
                </p>
              </div>
              <button onClick={() => setLooksGroup(null)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto p-3">
              {loadingLooks && looksGroup.looks.length === 0 && (
                <div className="p-12 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {looksGroup.looks.map((look) => {
                  const isSelected = selectedId === look.id;
                  return (
                    <button
                      key={look.id}
                      onClick={() => { onAvatarReady(look); setLooksGroup(null); }}
                      className={`group relative aspect-[3/4] rounded-xl overflow-hidden border-2 transition ${
                        isSelected
                          ? 'border-primary-500 ring-4 ring-primary-500/40'
                          : 'border-gray-200 dark:border-gray-700 hover:border-primary-500/60'
                      }`}
                    >
                      {look.preview_image_url ? (
                        <img src={look.preview_image_url} alt={look.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
                          <User2 className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      {isSelected && (
                        <span className="absolute top-1 right-1 w-6 h-6 rounded-full bg-primary-500 text-white text-xs font-bold inline-flex items-center justify-center shadow-lg">✓</span>
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-gray-950/95 to-transparent p-1.5">
                        <p className="text-[10px] font-semibold text-white truncate" title={look.name}>{look.name}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end">
              <button onClick={() => setLooksGroup(null)} className="px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300">
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <span>{groups.length} {t('videoStudio.avatar.ofTotal', 'de {{total}} avatares', { total })}</span>
        {page < totalPages && (
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Carregar mais 24
          </button>
        )}
      </div>

      {/* Preview modal — shows the HeyGen preview video (with controls, autoplay)
          or the photo at full size when no video is available. */}
      {previewAvatar && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-gray-950/85 backdrop-blur-sm"
          onClick={() => setPreviewAvatar(null)}
        >
          <div
            className="w-full max-w-2xl bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="text-white font-bold truncate">{previewAvatar.name}</h3>
                <p className="text-[11px] text-gray-400">
                  {previewAvatar.gender || t('videoStudio.avatar.noGender')}
                  {previewAvatar.is_yours && ' · ' + t('videoStudio.avatar.yourTag')}
                  {previewAvatar.premium && !previewAvatar.is_yours && ' · ' + t('videoStudio.avatar.proBadge')}
                </p>
              </div>
              <button onClick={() => setPreviewAvatar(null)} className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="bg-black flex items-center justify-center" style={{ maxHeight: '70vh' }}>
              {previewAvatar.preview_video_url ? (
                <video
                  src={previewAvatar.preview_video_url}
                  controls autoPlay loop muted
                  className="max-w-full max-h-[70vh]"
                />
              ) : previewAvatar.preview_image_url ? (
                <img
                  src={previewAvatar.preview_image_url}
                  alt={previewAvatar.name}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              ) : (
                <div className="p-12 text-gray-500">
                  <User2 className="w-16 h-16 mx-auto mb-2" />
                  <p className="text-sm">{t('videoStudio.avatar.noPreview')}</p>
                </div>
              )}
            </div>
            <div className="p-3 flex justify-end gap-2 border-t border-gray-800">
              <button
                onClick={() => setPreviewAvatar(null)}
                className="px-3 py-1.5 rounded-lg bg-white/5 text-white text-xs font-semibold hover:bg-white/10"
              >
                {t('common.close')}
              </button>
              <button
                onClick={() => { onAvatarReady(previewAvatar); setPreviewAvatar(null); }}
                className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-primary-500 to-purple-600 text-white text-xs font-semibold hover:brightness-110"
              >
                {t('videoStudio.avatar.usePreview')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create modal — Digital Twin (HeyGen) + Voice Clone (ElevenLabs) */}
      {showCreate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/80 backdrop-blur-sm" onClick={submitting ? undefined : () => setShowCreate(false)}>
          <div
            className="w-full max-w-2xl max-h-[92vh] flex flex-col bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900 dark:text-white">{t('videoStudio.avatar.create.title', 'Criar avatar profissional')}</h2>
                  <p className="text-[11px] text-gray-500">{t('videoStudio.avatar.create.subtitle', 'Vídeo + áudio da pessoa → avatar HeyGen + voz clonada (ElevenLabs)')}</p>
                </div>
              </div>
              <button onClick={() => setShowCreate(false)} disabled={submitting} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400 disabled:opacity-50">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* 1) Identity */}
              <section>
                <SectionTitle n={1} title={t('videoStudio.avatar.create.step1', 'Identidade do avatar') as string} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('videoStudio.avatar.create.name', 'Nome')}</label>
                    <input
                      type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                      placeholder={t('videoStudio.avatar.create.namePlaceholder', 'Ex: Marina — CEO') as string}
                      disabled={submitting}
                      className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('videoStudio.avatar.create.gender', 'Gênero')}</label>
                    <select
                      value={newGender} onChange={(e) => setNewGender(e.target.value as any)}
                      disabled={submitting}
                      className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                      <option value="unknown">{t('videoStudio.avatar.create.genderAny', 'Não especificar')}</option>
                      <option value="female">{t('videoStudio.avatar.create.genderF', 'Feminino')}</option>
                      <option value="male">{t('videoStudio.avatar.create.genderM', 'Masculino')}</option>
                    </select>
                  </div>
                </div>
              </section>

              {/* 2) Training video */}
              <section>
                <SectionTitle n={2} title={t('videoStudio.avatar.create.step2', 'Vídeo de treinamento') as string} required />
                <p className="text-[11px] text-gray-500 mb-2">
                  {t('videoStudio.avatar.create.step2Hint', 'A pessoa olhando pra câmera, falando naturalmente, sem cortes. Recomendado 2–5 min, ≥720p, fundo limpo, iluminação consistente. MP4 até 500MB.')}
                </p>
                <details className="mb-2 rounded-lg bg-primary-500/10 border border-primary-500/30 p-3 text-[11px] text-primary-900 dark:text-primary-200" open>
                  <summary className="font-semibold cursor-pointer">
                    {t('videoStudio.avatar.create.step2GuideTitle', 'Roteiro guia (opcional — segue isso pra melhor qualidade)')}
                  </summary>
                  <div className="mt-2 space-y-1.5 leading-relaxed whitespace-pre-line">
                    {t('videoStudio.avatar.create.trainingScript', `Olha pra câmera e fala naturalmente, como se estivesse conversando com um cliente. Use estas falas como guia:

1. "Olá! Eu sou [seu nome] e sou [sua função] na [sua empresa]."
2. "Hoje quero te mostrar como a gente pode resolver [problema do cliente]."
3. "Nos últimos anos, a gente já ajudou centenas de pessoas a conquistarem [resultado]."
4. (Pausa, expressão pensativa) "Você já parou pra pensar quanto tempo da sua semana é desperdiçado em coisas que poderiam ser automatizadas?"
5. (Sorriso) "É exatamente disso que vamos falar agora."
6. Conta uma história curta de cliente real (sem nome): "Lembro de um caso onde…"
7. Lista 3 benefícios em sequência, contando nos dedos.
8. Termina com um chamado pra ação: "Bora começar? Clique no botão abaixo."

Dicas: vire a cabeça suavemente algumas vezes, pisque normalmente, sorria de vez em quando. Não fique imóvel. NÃO precisa decorar — improvise se preferir.`)}
                  </div>
                </details>
                <FilePicker
                  file={trainingVideo}
                  accent="primary"
                  accept="video/mp4,video/quicktime,video/*"
                  icon="video"
                  emptyLabel={t('videoStudio.avatar.create.step2Cta', 'Selecionar vídeo de treinamento (MP4)') as string}
                  emptyHint={t('videoStudio.avatar.create.step2Range', '2–5 min · ≥720p · max 500MB') as string}
                  onChange={(f) => {
                    const err = validateVideoFile(f, 500); if (err) { toast.error(err); return; }
                    setTrainingVideo(f);
                  }}
                  onClear={() => setTrainingVideo(null)}
                  onRecord={() => setRecording('training')}
                  disabled={submitting}
                />
              </section>

              {/* 3) Consent video */}
              <section>
                <SectionTitle n={3} title={t('videoStudio.avatar.create.step3', 'Vídeo de consentimento') as string} required />
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 mb-2 text-[11px] text-amber-800 dark:text-amber-200">
                  <p className="font-semibold mb-1">{t('videoStudio.avatar.create.step3ScriptTitle', 'Grave (10–15s) a pessoa lendo este texto:')}</p>
                  <p className="italic">"{t('videoStudio.avatar.create.consentScript', 'Eu autorizo o uso da minha aparência e voz pela DevSkin Marketing e HeyGen para criar um avatar de IA.')}"</p>
                </div>
                <FilePicker
                  file={consentVideo}
                  accent="amber"
                  accept="video/mp4,video/quicktime,video/*"
                  icon="video"
                  emptyLabel={t('videoStudio.avatar.create.step3Cta', 'Selecionar vídeo de consentimento') as string}
                  emptyHint={t('videoStudio.avatar.create.step3Range', '10–15 segundos · MP4') as string}
                  onChange={(f) => {
                    const err = validateVideoFile(f, 100); if (err) { toast.error(err); return; }
                    setConsentVideo(f);
                  }}
                  onClear={() => setConsentVideo(null)}
                  onRecord={() => setRecording('consent')}
                  disabled={submitting}
                />
              </section>

              {/* 4) Voice audio (optional) */}
              <section>
                <SectionTitle n={4} title={t('videoStudio.avatar.create.step4', 'Áudio para clone de voz (ElevenLabs)') as string} />
                <p className="text-[11px] text-gray-500 mb-2">
                  {t('videoStudio.avatar.create.step4Hint', 'Opcional — gravação limpa de 1–3 min da pessoa falando (sem música, sem ruído). Vai virar uma voz clonada que você usa em qualquer roteiro depois. Sem isso, o avatar usa as vozes da biblioteca HeyGen.')}
                </p>
                <details className="mb-2 rounded-lg bg-violet-500/10 border border-violet-500/30 p-3 text-[11px] text-violet-900 dark:text-violet-200" open>
                  <summary className="font-semibold cursor-pointer">
                    {t('videoStudio.avatar.create.step4ScriptTitle', 'Texto pra leitura (3–4 min · cobre todos os fonemas pra clone perfeito)')}
                  </summary>
                  <div className="mt-2 leading-relaxed whitespace-pre-line max-h-64 overflow-y-auto pr-2">
                    {t('videoStudio.avatar.create.voiceScript', `Olá. Meu nome é [diga seu nome completo aqui]. Estou gravando este áudio para que minha voz seja usada em vídeos institucionais e materiais de marketing.

Vamos começar com algo simples. O sol nasce todos os dias no horizonte e ilumina nossas manhãs. Os pássaros cantam, as crianças brincam, e o trânsito da cidade começa cedo. Você consegue ouvir o ruído dos motores, o som das buzinas e o burburinho das pessoas indo ao trabalho.

Agora vamos para algo um pouco mais técnico. Em dois mil e vinte e seis, o setor de tecnologia movimentou trilhões de reais no Brasil. Empresas como Google, Microsoft e Amazon investiram pesado em inteligência artificial, computação em nuvem e cibersegurança.

Frases curtas com perguntas. Como você está? Tudo bem? Você concorda comigo? Que dia foi ontem? Você lembra do que falamos? Quanto custa esse produto? Onde fica o escritório? Quando podemos começar?

Vamos testar palavras com sons especiais. Pneumonia, psicologia, xícara, cachorro, churrasco, juventude, queijo, papagaio, esquadrilha, bicicleta, helicóptero, refrigerante, computador.

Números por extenso. Cento e vinte e três. Mil novecentos e oitenta e nove. Dezessete vírgula cinco. Trinta e dois mil e quatrocentos. Um milhão, trezentos e quarenta e cinco mil.

Datas e endereços. Hoje é segunda-feira, dia três de maio de dois mil e vinte e seis. Eu moro na rua das Acácias, número quatrocentos e cinquenta, apartamento doze, bairro Vila Mariana, São Paulo, SP, CEP zero quatro mil e cem traço cento e vinte.

Agora uma frase mais emotiva. (Surpresa) "Não acredito que isso aconteceu!" (Alegria) "Que notícia maravilhosa!" (Tristeza) "Lamento muito o ocorrido." (Calma) "Está tudo bem, vai dar certo." (Empolgação) "Vamos juntos fazer acontecer!"

Por último, uma fala mais corporativa. Nossa empresa nasceu com a missão de transformar a maneira como pessoas e negócios se conectam. Acreditamos que tecnologia bem aplicada gera resultados consistentes, escaláveis e mensuráveis. Estamos aqui para fazer parte da sua jornada.

Obrigado pela atenção. Tenha um excelente dia.`)}
                  </div>
                </details>
                <FilePicker
                  file={voiceAudio}
                  accent="violet"
                  accept="audio/mpeg,audio/mp3,audio/wav,audio/m4a,audio/webm,audio/*"
                  icon="audio"
                  emptyLabel={t('videoStudio.avatar.create.step4Cta', 'Selecionar áudio (MP3/WAV/M4A) — opcional') as string}
                  emptyHint={t('videoStudio.avatar.create.step4Range', '1–3 min · estúdio quieto · max 50MB') as string}
                  onChange={(f) => {
                    const err = validateAudioFile(f, 50); if (err) { toast.error(err); return; }
                    setVoiceAudio(f);
                  }}
                  onClear={() => setVoiceAudio(null)}
                  onRecord={() => setRecording('voice')}
                  disabled={submitting}
                />
              </section>

              {/* 5) Consent checkbox */}
              <section>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox" checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    disabled={submitting}
                    className="mt-0.5 rounded border-gray-300"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">
                    {t('videoStudio.avatar.create.consentCheck', 'Confirmo que tenho autorização da pessoa para criar este avatar e clone de voz, e que o uso seguirá os termos da HeyGen, ElevenLabs e DevSkin.')}
                  </span>
                </label>
              </section>

              {/* Cost preview */}
              <div className="rounded-xl bg-gradient-to-br from-gray-900/95 to-gray-950 p-4 border border-gray-800">
                <div className="flex items-center gap-2 text-gray-300 text-[10px] uppercase tracking-wider font-semibold mb-2">
                  <Sparkles className="w-3 h-3" /> {t('videoStudio.avatar.create.costTitle', 'Custo estimado')}
                </div>
                <div className="space-y-1.5 text-xs">
                  <CostLine
                    label={t('videoStudio.avatar.create.costAvatar', 'Avatar HeyGen (Digital Twin)') as string}
                    value={`${creditos(custoAvatar)} `} unit={t('videoStudio.heygen.credits', 'créditos') as string}
                  />
                  <CostLine
                    label={t('videoStudio.avatar.create.costVoice', 'Voz ElevenLabs (clone)') as string}
                    value={voiceAudio ? '~$1' : '0'}
                    unit={voiceAudio ? '' : (t('videoStudio.avatar.create.costFree', 'sem áudio = sem clone') as string)}
                    free={!voiceAudio}
                  />
                  <div className="border-t border-gray-800 pt-1.5 mt-2 flex items-center justify-between">
                    <span className="text-white font-semibold">{t('videoStudio.avatar.create.costTotal', 'Total da sua conta DevSkin')}</span>
                    <span className="text-2xl font-bold text-white">{creditos(custoAvatar)} <span className="text-xs text-gray-400 font-normal">{t('videoStudio.heygen.credits', 'créditos')}</span></span>
                  </div>
                </div>
                <p className="text-[10px] text-gray-500 mt-2">
                  {t('videoStudio.avatar.create.costHint', 'Treinamento leva ~5–15min. Você é notificado quando ficar pronto. Falhas são reembolsadas automaticamente.')}
                </p>
              </div>

              {submitting && (
                <div>
                  <div className="flex items-center justify-between text-[11px] mb-1">
                    <span className="text-gray-500">{t('videoStudio.avatar.create.uploading', 'Enviando arquivos…')}</span>
                    <span className="text-gray-700 dark:text-gray-300 tabular-nums">{uploadProgress}%</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-primary-500 to-purple-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowCreate(false)} disabled={submitting}
                className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 disabled:opacity-50"
              >
                {t('common.close')}
              </button>
              <button
                onClick={handleSubmitCreate}
                disabled={submitting || !newName.trim() || !trainingVideo || !consentVideo || !consentChecked}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary-500 to-purple-600 text-white text-sm font-semibold inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-50"
              >
                {submitting
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('videoStudio.avatar.create.creating', 'Criando…')}</>
                  : <><Camera className="w-4 h-4" /> {t('videoStudio.avatar.create.cta', 'Criar avatar profissional')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* In-browser recorder panel — opens when the user clicks "Gravar agora"
          on any of the 3 file slots inside the create modal. */}
      {recording && (
        <MediaRecorderPanel
          mode={recording === 'voice' ? 'audio' : 'video'}
          title={
            recording === 'training'
              ? (t('videoStudio.avatar.create.step2', 'Vídeo de treinamento') as string)
              : recording === 'consent'
                ? (t('videoStudio.avatar.create.step3', 'Vídeo de consentimento') as string)
                : (t('videoStudio.avatar.create.step4', 'Áudio para clone de voz (ElevenLabs)') as string)
          }
          hintSeconds={
            recording === 'training' ? { min: 120, max: 300 }
            : recording === 'consent' ? { min: 10, max: 30 }
            : { min: 60, max: 180 }
          }
          scriptText={
            recording === 'consent'
              ? (t('videoStudio.avatar.create.consentScript', 'Eu autorizo o uso da minha aparência e voz pela DevSkin Marketing e HeyGen para criar um avatar de IA.') as string)
              : recording === 'voice'
                ? (t('videoStudio.avatar.create.voiceScript', '') as string)
                : recording === 'training'
                  ? (t('videoStudio.avatar.create.trainingScript', '') as string)
                  : undefined
          }
          onCancel={() => setRecording(null)}
          onCapture={(file) => {
            if (recording === 'training') setTrainingVideo(file);
            else if (recording === 'consent') setConsentVideo(file);
            else setVoiceAudio(file);
            setRecording(null);
          }}
        />
      )}
    </div>
  );
};

// ─── Modal helpers ────────────────────────────────────────────────────────────

const SectionTitle: React.FC<{ n: number; title: string; required?: boolean }> = ({ n, title, required }) => (
  <div className="flex items-center gap-2">
    <span className="w-5 h-5 rounded-full bg-primary-500 text-white text-[10px] font-bold inline-flex items-center justify-center">{n}</span>
    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}{required && <span className="text-rose-500 ml-1">*</span>}</h3>
  </div>
);

const FilePicker: React.FC<{
  file: File | null;
  accept: string;
  emptyLabel: string;
  emptyHint: string;
  icon: 'video' | 'audio';
  accent: 'primary' | 'amber' | 'violet';
  disabled?: boolean;
  onChange: (file: File) => void;
  onClear: () => void;
  /** Optional in-browser recorder trigger — shown as a second button next to upload */
  onRecord?: () => void;
}> = ({ file, accept, emptyLabel, emptyHint, icon, accent, disabled, onChange, onClear, onRecord }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const accentClass = accent === 'amber'
    ? 'border-amber-400/50 hover:border-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
    : accent === 'violet'
      ? 'border-violet-400/50 hover:border-violet-400 bg-violet-50/50 dark:bg-violet-900/10'
      : 'border-primary-400/50 hover:border-primary-400 bg-primary-50/50 dark:bg-primary-900/10';
  if (file) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
          accent === 'amber' ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
          : accent === 'violet' ? 'bg-violet-500/20 text-violet-700 dark:text-violet-300'
          : 'bg-primary-500/20 text-primary-700 dark:text-primary-300'
        }`}>
          {icon === 'video' ? <Camera className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">{file.name}</p>
          <p className="text-[10px] text-gray-500 font-mono">
            {(file.size / 1024 / 1024).toFixed(1)}MB · {file.type || 'unknown'}
          </p>
        </div>
        <button onClick={onClear} disabled={disabled} className="p-1.5 rounded text-rose-500 hover:bg-rose-500/10 disabled:opacity-50">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }
  return (
    <div className={`p-5 rounded-lg border-2 border-dashed transition ${accentClass}`}>
      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 text-center mb-1">{emptyLabel}</p>
      <p className="text-[10px] text-gray-500 text-center mb-3">{emptyHint}</p>
      <div className={`grid gap-2 ${onRecord ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <button
          type="button" disabled={disabled} onClick={() => inputRef.current?.click()}
          className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Upload className="w-3.5 h-3.5" /> {t('videoStudio.recorder.uploadFile', 'Enviar arquivo')}
        </button>
        {onRecord && (
          <button
            type="button" disabled={disabled} onClick={onRecord}
            className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {icon === 'video' ? <Video className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
            {t('videoStudio.recorder.recordNow', 'Gravar agora')}
          </button>
        )}
      </div>
      <input
        ref={inputRef} type="file" accept={accept} className="hidden"
        onChange={(e) => e.target.files?.[0] && onChange(e.target.files[0])}
      />
    </div>
  );
};

const CostLine: React.FC<{ label: string; value: string; unit?: string; free?: boolean }> = ({ label, value, unit, free }) => (
  <div className="flex items-center justify-between text-gray-300">
    <span>{label}</span>
    <span className={free ? 'text-gray-500' : 'text-white font-mono tabular-nums'}>
      {value}{unit && <span className="ml-1 text-gray-500">{unit}</span>}
    </span>
  </div>
);

export default AvatarStudio;
